import * as React from 'react';

import { CalendarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export function DatePicker({
  value,
  onChange,
  className,
  placeholder,
  ...props
}: {
  value: Date | undefined;
  onChange: (value: Date | undefined) => void;
  className?: string;
  placeholder?: string;
} & Omit<React.ComponentPropsWithoutRef<typeof Button>, 'value' | 'onChange'>) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            variant="outline"
            className={cn(
              'hover:border-ring bg-background h-11 w-full justify-start rounded-md text-base font-normal md:text-sm',
              className,
            )}
            {...props}
          >
            {value
              ? value.toLocaleDateString()
              : (placeholder ?? t('common.selectDate'))}

            <CalendarIcon className="ml-auto" />
          </Button>
        }
      />
      <PopoverContent className="w-auto overflow-hidden p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          defaultMonth={value}
          captionLayout="dropdown"
          onSelect={(date) => {
            onChange(date);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
